/**
 * Password Policy Validation
 *
 * Requirements:
 * - Minimum 8 characters
 * - At least 1 letter (a-z or A-Z)
 * - At least 1 number (0-9)
 * - At least 1 special character (!@#$%^&*()_+-=[]{}|;:'",.<>?/`~)
 * - Passwords are case-sensitive (enforced by default, no normalization)
 */

const POLICY = {
  minLength: 8,
  requireLetter: true,
  requireNumber: true,
  requireSpecialChar: true,
};

/**
 * Validate a password against the policy.
 * Returns { valid: boolean, errors: string[] }
 */
export function validatePassword(password) {
  const errors = [];

  if (!password || password.length < POLICY.minLength) {
    errors.push(`Must be at least ${POLICY.minLength} characters`);
  }

  if (POLICY.requireLetter && !/[a-zA-Z]/.test(password)) {
    errors.push('Must include at least 1 letter');
  }

  if (POLICY.requireNumber && !/[0-9]/.test(password)) {
    errors.push('Must include at least 1 number');
  }

  if (POLICY.requireSpecialChar && !/[^a-zA-Z0-9]/.test(password)) {
    errors.push('Must include at least 1 special character');
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Get human-readable password requirements string
 */
export function getPasswordRequirements() {
  return `Min. ${POLICY.minLength} chars, at least 1 letter, 1 number, and 1 special character. Case-sensitive.`;
}

/**
 * Get password strength (0-4 scale)
 */
export function getPasswordStrength(password) {
  if (!password) return 0;
  let score = 0;
  if (password.length >= POLICY.minLength) score++;
  if (/[a-zA-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;
  return score;
}

export const PASSWORD_STRENGTH_LABELS = ['', 'Weak', 'Fair', 'Good', 'Strong'];
export const PASSWORD_STRENGTH_COLORS = ['#d6d3d1', '#ef4444', '#d97706', '#78716c', '#0d9488'];
